<template>
  <v-form
    ref="formRef"
    class="fan-form method-form"
    @submit.prevent=""
  >
    <!-- Basisdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-counter</v-icon>
        <span>Basisdaten</span>
      </div>

      <!-- Anzahl -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        :error-messages="validationErrors?.amount"
        label="Anzahl"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.amount"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Anzahl übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Luft- und Druckdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-fan</v-icon>
        <span>Luft- und Druckdaten</span>
      </div>

      <!-- Luftmenge -->
      <v-text-field
        v-model.number="form.volumeFlow"
        type="number"
        :error-messages="validationErrors?.volume_flow"
        label="Luftmenge"
        suffix="m³/h"
        :hint="hint.volume_flow"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.volume_flow" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.volume_flow }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.volume_flow"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Luftmenge übernehmen"
            @click="copyFromReference('volume_flow')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Gesamtdruck -->
      <v-text-field
        v-model.number="form.totalPressure"
        type="number"
        :error-messages="validationErrors?.total_pressure"
        label="Gesamtdruck"
        suffix="Pa"
        :hint="hint.total_pressure"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.total_pressure" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.total_pressure }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.total_pressure"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Gesamtdruck übernehmen"
            @click="copyFromReference('total_pressure')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Betrieb -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betrieb</span>
      </div>

      <!-- Jahresbetriebsstunden -->
      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Jährliche Betriebsstunden"
        suffix="h/a"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Betriebsstunden übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Bestandsdaten: reference only -->
    <div v-if="reference" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-calendar-clock-outline</v-icon>
        <span>Bestandsdaten</span>
      </div>

      <!-- Baujahr -->
      <v-text-field
        v-model.number="form.yearBuilt"
        type="number"
        :error-messages="validationErrors?.year_built"
        label="Baujahr"
        :hint="hint.year_built"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.year_built" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.year_built }}</span>
          </v-tooltip>
        </template>
      </v-text-field>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
// import { useProjectStore } from '@/stores/projectStore'
// import { storeToRefs } from 'pinia'

/* Props */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Pinia (for generator list loading state) */
// const projectStore = useProjectStore()
// const { currentProjectActions: actionList } = storeToRefs(projectStore)

/* Field mapping */
const fields = {
  actionId:      'action_id',
  amount:        'amount',
  volumeFlow:    'volume_flow',
  totalPressure: 'total_pressure',
  yearBuilt:     'year_built',
  uptime:        'uptime',
}

/* useBaseForm */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {},         // no special defaults
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => false),
  otherData: computed(() => props.otherData),
  sharedData:computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* validate() exposure for parent */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}


defineExpose({
  validateForm,
  fields: Object.values(fields),
})


/* Fetch hints (generator-based help) */
fetchHints()
</script>

<style scoped lang="scss">
.fan-form {
  width: 100%;
}

/* Input fields */
.fan-form :deep(.v-field) {
  border-radius: 18px;
}

.fan-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

/* Help and reference-copy actions */
.fan-form :deep(.v-input__append),
.fan-form :deep(.v-field__append-inner) {
  align-items: center;
}

.fan-form :deep(.v-input__append .v-icon),
.fan-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.fan-form :deep(.v-input__append .v-btn),
.fan-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.fan-form :deep(.v-input__append .v-btn:hover),
.fan-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled states */
.fan-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }
}
</style>
